import { cn } from "@/lib/utils"
import { cva, type VariantProps } from "class-variance-authority"
import { forwardRef } from "react"

export function ArrowRightIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.8563 6.23867C14.1536 5.92044 14.6354 5.92045 14.9326 6.23867L19.7771 11.4228C20.0743 11.741 20.0743 12.259 19.7771 12.5772L14.9326 17.7613C14.6354 18.0796 14.1536 18.0796 13.8563 17.7613C13.5591 17.4431 13.5591 16.9273 13.8563 16.6091L17.4005 12.8149L4.81494 12.8149C4.36484 12.8149 4 12.45 4 12C4 11.55 4.36484 11.1851 4.81494 11.1851L17.4005 11.1851L13.8563 7.39093C13.5591 7.07271 13.5591 6.55689 13.8563 6.23867Z"
        fill="currentColor"
      />
    </svg>
  )
}

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aubergine-400 disabled:pointer-events-none disabled:cursor-not-allowed cursor-pointer type-title-md",
  {
    variants: {
      variant: {
        primary:
          "text-white bg-aubergine-800 hover:shadow-[inset_0_0_0_999px_rgba(255,255,255,0.08)] disabled:bg-neutral-200 disabled:text-neutral-400",
        "primary-alt":
          "text-white bg-bright-blue-950 hover:shadow-[inset_0_0_0_999px_rgba(255,255,255,0.08)] disabled:bg-neutral-200 disabled:text-neutral-400",
        tonal:
          "text-neutral-900 bg-neutral-100 hover:shadow-[inset_0_0_0_999px_rgba(0,0,0,0.03)] disabled:bg-neutral-100/60 disabled:text-neutral-400",
        outline:
          "text-neutral-900 bg-transparent border border-neutral-300 hover:bg-neutral-900/5 hover:border-neutral-300 disabled:bg-neutral-900/5 disabled:text-neutral-400 disabled:border-neutral-300/60",
        text: "text-neutral-900 bg-transparent hover:bg-white/10 disabled:text-neutral-400",
      },
      shape: {
        round: "rounded-full",
        square: "rounded-[12px]",
      },
      size: {
        default: "h-[44px] px-8 py-[10px]",
        sm: "h-[36px] px-4 py-1.5 type-body-md",
        lg: "h-[52px] px-10 py-3 type-body-lg",
      },
    },
    defaultVariants: { variant: "primary", shape: "round", size: "default" },
  }
)

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      shape,
      size,
      iconLeft,
      iconRight,
      children,
      ...props
    },
    ref
  ) => (
    <button
      className={cn(
        buttonVariants({ variant, shape, size }),
        iconRight && !iconLeft && "pr-6",
        iconLeft && !iconRight && "pl-6",
        className
      )}
      ref={ref}
      {...props}
    >
      {iconLeft}
      {children}
      {iconRight}
    </button>
  )
)
Button.displayName = "Button"
